/**
 * Accounts and project persistence.
 *
 * The session cookie is HttpOnly and set by the server, so nothing here reads
 * or stores a token — `me` is the only way to learn who is signed in, and a
 * 401 from any call means the session lapsed.
 */

import type { CadDocument, DocumentKind } from '@sircad/core/model.ts';
import { apiGet, apiPost } from './api.ts';

/**
 * What a signed-in person may do.
 *
 * - `admin`    — us: sees every user and every report.
 * - `manager`  — buys the seats, creates operators, is billed for the account.
 * - `operator` — draws; sees only their own reports and nothing about billing.
 */
export type Role = 'admin' | 'manager' | 'operator';

export interface User {
  id: number;
  email: string;
  display_name: string;
  plan: string;
  licensed_until: string | null;
  is_admin: boolean;
  role: Role;
  account_id: number | null;
  /** Whether to offer the people and billing screens at all. */
  can_manage: boolean;
}

/** The paying entity: one or more managers and the operators they create. */
export interface Account {
  id: number;
  name: string;
  plan: string;
  seats: number;
  seats_used: number;
  licensed_until: string | null;
  primary_user_id: number | null;
  billing_email: string;
  gateway: string;
  is_primary: boolean;
}

export interface FeedbackSummary {
  id: number;
  kind: 'bug' | 'feature';
  title: string;
  status: 'new' | 'open' | 'closed';
  created_at: string;
  app_version: string;
  user_id: number;
  email: string;
  display_name: string;
  files: number;
  excerpt: string;
}

/** One attached drawing, held as native CIRCAD text. */
export interface FeedbackFile {
  id: number;
  name: string;
  kind: DocumentKind;
  content: string;
}

/**
 * One report in full.
 *
 * `files` is the drawings themselves here, where the summary carries only how
 * many there are — the list view would otherwise download every board on it.
 */
/** One message in a report's thread, from either side. */
export interface FeedbackReply {
  id: number;
  body: string;
  /** 1 when written by staff. Decided when written: roles change. */
  from_staff: number;
  created_at: string;
  display_name: string;
  email: string;
}

export interface FeedbackDetail extends Omit<FeedbackSummary, 'files' | 'excerpt'> {
  body: string;
  context: string;
  files: FeedbackFile[];
  replies: FeedbackReply[];
}

export interface ManagedUser {
  id: number;
  email: string;
  display_name: string;
  role: Role;
  is_admin: number;
  account_id: number | null;
  created_at: string;
  last_login_at: string | null;
  account_name: string | null;
  plan: string | null;
  seats: number | null;
  licensed_until: string | null;
  is_primary: number;
  feedback: number;
}

export interface ProjectSummary {
  id: number;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  documents: number;
}

/**
 * A text file kept in a project. Not a drawing, so not a CadDocument — the
 * server stores the payload opaquely and never looks inside either kind.
 */
export interface TextDocumentData {
  kind: 'txt';
  /** Tab name. */
  name: string;
  /** File name, which the editor guesses the language from. */
  file: string;
  text: string;
  /** Language chosen by hand, where one was. */
  lang?: string;
}

/** What a project row may hold. */
export type StoredKind = DocumentKind | 'txt';
export type StoredDocument = CadDocument | TextDocumentData;

export interface DocumentRow {
  id: number;
  kind: StoredKind;
  name: string;
  sheet: number;
  data: StoredDocument;
  updated_at: string;
}

/** One entry in a document's saved history. The body is fetched separately. */
export interface RevisionSummary {
  id: number;
  note: string;
  created_at: string;
  bytes: number;
}

export interface LoadedProject {
  project: ProjectSummary;
  documents: DocumentRow[];
}

export const Session = {
  async current(): Promise<User | null> {
    const r = await apiGet<{ user: User | null }>('me');
    return r.user;
  },

  async register(email: string, password: string, name: string): Promise<User> {
    const r = await apiPost<{ id: number; user: User }>('register', {
      email, password, name,
    });
    return r.user;
  },

  async login(email: string, password: string): Promise<User> {
    const r = await apiPost<{ user: User }>('login', { email, password });
    return r.user;
  },

  async logout(): Promise<void> {
    await apiPost('logout');
  },
};

export const Projects = {
  list(): Promise<ProjectSummary[]> {
    return apiGet<ProjectSummary[]>('projects');
  },

  create(name: string, description = ''): Promise<LoadedProject> {
    return apiPost<LoadedProject>('create_project', { name, description });
  },

  open(id: number): Promise<LoadedProject> {
    return apiGet<LoadedProject>('project', { id });
  },

  rename(id: number, name: string, description = ''): Promise<void> {
    return apiPost('rename_project', { id, name, description });
  },

  remove(id: number): Promise<void> {
    return apiPost('delete_project', { id });
  },

  /** Overwrite a document. The server keeps the previous state as a revision. */
  saveDocument(id: number, document: StoredDocument, note = ''): Promise<{ bytes: number }> {
    return apiPost<{ bytes: number }>('save_document', {
      id, document, name: document.name, note,
    });
  },

  /**
   * Add a document to a project — a further schematic sheet, say. The server
   * assigns the next sheet number for that kind.
   */
  addDocument(
    projectId: number, kind: StoredKind, name: string, document: StoredDocument,
  ): Promise<{ id: number }> {
    return apiPost<{ id: number }>('add_document', {
      project_id: projectId, kind, name, document,
    });
  },

  /** Remove a document (a sheet) from its project. */
  removeDocument(id: number): Promise<void> {
    return apiPost('delete_document', { id });
  },

  /**
   * What this document looked like before each of its recent saves, newest
   * first.
   *
   * The server writes one of these every time a document is saved, before
   * overwriting it, and prunes to the newest fifty. That is what makes undo
   * outlive the tab: the in-memory stack is gone when the page is, and this is
   * not. Bodies are not fetched — the list is ids, notes and sizes, so asking
   * what is available costs nothing.
   */
  revisions(id: number): Promise<RevisionSummary[]> {
    return apiGet<RevisionSummary[]>('revisions', { id });
  },

  /** One revision's document, by its id from {@link revisions}. */
  revision(id: number): Promise<{ document: StoredDocument; created_at: string }> {
    return apiGet<{ document: StoredDocument; created_at: string }>('revision', { id });
  },
};

/** Bug reports and feature requests. */
export const Feedback = {
  /**
   * Send a report, with the drawings that were open.
   *
   * The files travel as native CIRCAD text rather than as the internal JSON:
   * a report that can be saved out and opened in the original program is worth
   * more than one that can only be read back by the version that wrote it.
   */
  submit(report: {
    kind: 'bug' | 'feature';
    title: string;
    body: string;
    context: string;
    app_version: string;
    files: { name: string; kind: DocumentKind; content: string }[];
  }): Promise<{ id: number; files: number }> {
    return apiPost<{ id: number; files: number }>('feedback_submit', report);
  },

  /** Every report for staff; your own otherwise. */
  list(filter: { status?: string; user_id?: number } = {}): Promise<FeedbackSummary[]> {
    const params: Record<string, string | number> = {};
    if (filter.status) params.status = filter.status;
    if (filter.user_id) params.user_id = filter.user_id;
    return apiGet<FeedbackSummary[]>('feedback_list', params);
  },

  /** One report in full, including the attached drawings. */
  get(id: number): Promise<FeedbackDetail> {
    return apiGet<FeedbackDetail>('feedback_get', { id });
  },

  setStatus(id: number, status: 'new' | 'open' | 'closed'): Promise<void> {
    return apiPost('feedback_status', { id, status });
  },

  /**
   * Answer a report.
   *
   * Staff can answer any; anyone else only their own. Replying reopens a
   * report that had been closed, so an answer never lands somewhere nobody is
   * looking.
   */
  reply(id: number, body: string): Promise<void> {
    return apiPost('feedback_reply', { id, body });
  },

  /** Delete a report, its drawings and its thread. Staff only. */
  remove(id: number): Promise<void> {
    return apiPost('feedback_delete', { id });
  },
};

/** People and billing. */
export const People = {
  /** Every user for staff; the caller's own account otherwise. */
  list(): Promise<ManagedUser[]> {
    return apiGet<ManagedUser[]>('users_list');
  },

  setRole(id: number, role: Role): Promise<void> {
    return apiPost('set_role', { id, role });
  },

  createOperator(email: string, password: string, name: string): Promise<{ id: number }> {
    return apiPost<{ id: number }>('create_operator', { email, password, name });
  },

  async account(): Promise<Account | null> {
    const r = await apiGet<{ account: Account | null }>('account');
    return r.account;
  },
};
